import { retryAfterUnauthorized } from "./session";
import { API_BASE_URL } from "../config";

/**
 * Risk Analysis Streaming API Service
 *
 * Handles Server-Sent Events (SSE) connections for real-time stage risk analysis streaming.
 * Provides methods to analyze workflow stage risks with streaming updates.
 */

/**
 * Get authentication headers
 * @returns {object} Headers object with auth token
 */
const getAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  // Get auth token from localStorage or sessionStorage
  const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");

  if (user.token) {
    headers["Authorization"] = `Bearer ${user.token}`;
  }

  return headers;
};

/**
 * Analyze stage risks with streaming updates
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number to analyze
 * @param {object} options - Analysis options
 * @param {string} options.userQuery - Optional user query for the analysis (default: "Analyze stage risks and errors")
 * @param {boolean} options.enableWebValidation - Enable web validation (default: false)
 * @param {function} options.onEvent - Callback for each SSE event (event) => void
 * @param {function} options.onError - Callback for errors (error) => void
 * @param {function} options.onComplete - Callback when analysis completes () => void
 * @param {function} options.onConnectionOpen - Callback when connection opens () => void
 * @param {AbortSignal} options.signal - AbortSignal to cancel the request
 * @returns {object} Controller with abort() method
 */
export function analyzeStageRisksStream(workflowId, stageNumber, options = {}) {
  const {
    userQuery = "Analyze stage risks and errors",
    enableWebValidation = false,
    onEvent,
    onError,
    onComplete,
    onConnectionOpen,
    signal,
  } = options;

  // Validate required parameters
  if (!workflowId || typeof workflowId !== "string") {
    const error = new Error("Invalid workflow ID");
    if (onError) {
      onError(error);
    }
    throw error;
  }

  if (!stageNumber || typeof stageNumber !== "number") {
    const error = new Error("Invalid stage number");
    if (onError) {
      onError(error);
    }
    throw error;
  }

  // Build request payload
  const payload = {
    user_query: userQuery,
    enable_web_validation: enableWebValidation,
  };

  // Get auth headers
  const headers = getAuthHeaders();

  // Create URL for SSE endpoint
  const url = `${API_BASE_URL}/api/risk-error/analyze-stage-stream/${workflowId}/${stageNumber}`;

  const controller = new AbortController();
  const fetchSignal = signal || controller.signal;

  // Start streaming request
  (async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
        signal: fetchSignal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP error! status: ${response.status}`,
        }));

        // Handle 401 Unauthorized - Token expired
        if (response.status === 401) {
          // the access token may simply have expired: renew it, then the caller retries

          await retryAfterUnauthorized(() => undefined);
          throw new Error(errorData.detail || "Session expired. Please login again.");
        }

        // Handle 404 - Feature not enabled
        if (response.status === 404) {
          throw new Error(
            "Risk and error detection feature is not enabled. Please enable it in your organization settings."
          );
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Connection opened successfully
      if (onConnectionOpen) {
        onConnectionOpen();
      }

      // Read the response stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Stream complete
          if (onComplete) {
            onComplete();
          }
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (end with double newline)
        const lines = buffer.split("\n\n");

        // Keep the last incomplete message in buffer
        buffer = lines.pop() || "";

        // Process each complete message
        for (const line of lines) {
          if (line.trim()) {
            // Parse SSE format: "data: {json}"
            const dataMatch = line.match(/^data:\s*(.+)$/m);

            if (dataMatch && dataMatch[1]) {
              try {
                const eventData = JSON.parse(dataMatch[1]);

                // Call event handler
                if (onEvent) {
                  onEvent(eventData);
                }

                // Check if analysis is complete
                if (eventData.event_type === "analysis_complete") {
                  if (onComplete) {
                    onComplete();
                  }
                }
              } catch (parseError) {
                console.error("Error parsing risk SSE event:", parseError, dataMatch[1]);
                if (onError) {
                  onError(new Error("Failed to parse event data"));
                }
              }
            }
          }
        }
      }
    } catch (error) {
      // Only report non-abort errors
      if (error.name !== "AbortError") {
        console.error("Risk streaming error:", error);
        if (onError) {
          onError(error);
        }
      }
    }
  })();

  // Return controller for cancellation
  return {
    abort: () => controller.abort(),
    signal: controller.signal,
  };
}

/**
 * Analyze stage risks with simplified promise-based interface
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number
 * @param {string} userQuery - User query (optional)
 * @param {boolean} enableWebValidation - Enable web validation
 * @returns {Promise} Promise that resolves with array of all events
 */
export function analyzeStageRisksStreamPromise(
  workflowId,
  stageNumber,
  userQuery = "Analyze stage risks and errors",
  enableWebValidation = false
) {
  return new Promise((resolve, reject) => {
    const events = [];

    analyzeStageRisksStream(workflowId, stageNumber, {
      userQuery,
      enableWebValidation,
      onEvent: (event) => {
        events.push(event);
      },
      onError: (error) => {
        reject(error);
      },
      onComplete: () => {
        resolve(events);
      },
    });
  });
}

/**
 * Test connection to risk streaming endpoint (useful for debugging)
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number
 * @returns {Promise<boolean>} True if endpoint is reachable
 */
export async function testRiskStreamingConnection(workflowId, stageNumber) {
  try {
    const url = `${API_BASE_URL}/api/risk-error/analyze-stage-stream/${workflowId}/${stageNumber}`;
    const headers = getAuthHeaders();

    // Send OPTIONS request to test connectivity
    const response = await fetch(url, {
      method: "OPTIONS",
      headers: headers,
    });

    return response.ok;
  } catch (error) {
    console.error("Risk streaming connection test failed:", error);
    return false;
  }
}

/**
 * Check if risk and error detection is enabled for organization
 *
 * @returns {Promise<boolean>} True if feature is enabled
 */
export async function checkRiskDetectionEnabled() {
  try {
    const url = `${API_BASE_URL}/api/risk-error/status`;
    const headers = getAuthHeaders();

    const response = await fetch(url, {
      method: "GET",
      headers: headers,
    });

    if (response.status === 404) {
      return false; // Feature not enabled
    }

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.enabled === true;
  } catch (error) {
    console.error("Error checking risk detection status:", error);
    return false;
  }
}

const riskStreamApi = {
  analyzeStageRisksStream,
  analyzeStageRisksStreamPromise,
  testRiskStreamingConnection,
  checkRiskDetectionEnabled,
};

export default riskStreamApi;
